import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { Autocomplete, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { deleteObjectApi } from 'api/general';
import { DECISION_FILE } from 'app/utils/constants/customConstant';
import { useEffect, useState } from 'react';
import DzPreviews from '../DzPreviews';
import AgendaFileListTable from '../Table/AgendaFileListTable';
import { fileTypes } from '../../../config';
import Div from '@jumbo/shared/Div';
import { useSelector, useDispatch } from 'react-redux';
import { postUploadAgendaFileApi } from 'api/meeting';
import { updateMeetingAfterUserOperation } from 'app/utils/meetingReduxHelpers';
import Swal from 'sweetalert2';

function AgendaFileCard({ agendaFileList, setAgendaFileList }) {
    const dispatch = useDispatch();
    const [previewFile, setPreviewFile] = useState(null);
    const [linkInfo, setLinkInfo] = useState('');
    const currentMeeting = useSelector((state) => state.meeting.currentMeeting);
    const currentAgenda = useSelector((state) => state.meeting.currentAgenda);
    const { authUser } = useSelector((state) => state.auth);
    const allMeetingList = useSelector((state) => state.meeting.allMeetingList);
    const allMMList = useSelector((state) => state.meeting.allMMList);
    const [selectedFileType, setSelectedFileType] = useState(null);
    const [loadingFile, setLoadingFile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [customFileType, setCustomFileType] = useState('');
    const [showValidation, setShowValidation] = useState(false);

    useEffect(() => {
        if (agendaFileList) {
            setAgendaFileList(agendaFileList);
        }
        setLoading(false);
    }, [agendaFileList, setAgendaFileList]);

    const uploadAgendaFile = async () => {
        setShowValidation(true); // Validation mesajlarını göster

        if (!selectedFileType) {
            console.error('Dosya tipi seçilmedi');
            return;
        }

        if ((selectedFileType.name === 'Diğer' || selectedFileType.name === 'Bağlantı') && !customFileType.trim()) {
            console.error('Dosya adı/bağlantı ismi girilmedi');
            return;
        }

        if (selectedFileType.name === 'Bağlantı' && !linkInfo.trim()) {
            console.error('Bağlantı adresi girilmedi');
            return;
        }

        if (selectedFileType.name !== 'Bağlantı' && !previewFile) {
            console.error('Dosya seçilmedi');
            return;
        }

        setLoadingFile(true);
        const formData = new FormData();
        let fileTypeName = (selectedFileType?.name === 'Diğer' || selectedFileType?.name === 'Bağlantı')
            ? customFileType.trim()
            : selectedFileType?.name;


        formData.append('fileType', fileTypeName || '');
        formData.append('adminUserId', authUser.id);
        formData.append('decisionId', currentAgenda.id);
        formData.append('meetingId', currentAgenda.meetingId);
        formData.append('inst', currentMeeting.inst);

        if (selectedFileType?.name === 'Bağlantı') {
            formData.append('fileInfo', linkInfo.trim());
        } else {
            formData.append('file', previewFile);
        }

        try {
            const result = await postUploadAgendaFileApi(formData);
            if (result.status) {
                const newItem = result.data[0];
                newItem.user = authUser;
                setAgendaFileList(prev => [newItem, ...prev]);
                setCustomFileType('');
                setPreviewFile(null);
                if (selectedFileType?.name === 'Bağlantı') setLinkInfo('');
                else setPreviewFile(null);
                setShowValidation(false); // Başarılı upload sonrası validation mesajlarını gizle

                if (authUser.id !== currentMeeting.adminUserId) {
                    await updateMeetingAfterUserOperation(
                        currentMeeting,
                        authUser,
                        dispatch,
                        allMeetingList,
                        allMMList
                    );
                }

                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: 'Dosya yükleme başarılı.',
                    showConfirmButton: false,
                    timer: 1800,
                    didOpen: () => {
                        const container = Swal.getContainer();
                        if (container) {
                            container.style.zIndex = '20000';
                        }
                    }
                });
            }
        } catch (err) {
            console.error('Upload error', err);
        }

        setLoadingFile(false);
    };

    const handleDeleteFile = async (item) => {
        const confirmResult = await Swal.fire({
            title: 'Dosya Silme İşlemi',
            text: 'Dosya silinecek. Onaylıyor musunuz?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Evet',
            cancelButtonText: 'Vazgeç',
            didOpen: () => {
                const container = Swal.getContainer();
                if (container) {
                    container.style.zIndex = '20000';
                }
            }
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        const result = await deleteObjectApi(DECISION_FILE, item.id);
        if (result.status) {
            setAgendaFileList(list => list.filter(x => x.id !== item.id));

            if (authUser.id !== currentMeeting.adminUserId) {
                await updateMeetingAfterUserOperation(
                    currentMeeting,
                    authUser,
                    dispatch,
                    allMeetingList,
                    allMMList
                );
            }

            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Dosya silme başarılı.',
                showConfirmButton: false,
                timer: 1800,
                didOpen: () => {
                    const container = Swal.getContainer();
                    if (container) {
                        container.style.zIndex = '20000';
                    }
                }
            });
        }
    };

    const fontSettings = useSelector((state) => state.meeting.fontSettings);
    const { baseFontSize } = fontSettings;

    return (
        <>
            <JumboCardQuick>
                <Grid container spacing={0.5}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontSize: baseFontSize }}>
                            Dosya eklemek için önce dosya tipini seçiniz
                        </Typography>
                    </Grid>

                    <Grid item xs={4}>
                        <Autocomplete
                            size="small"
                            options={fileTypes}
                            getOptionLabel={(option) => option.name}
                            onChange={(_, newValue) => {
                                setSelectedFileType(newValue);
                                if (newValue?.name !== 'Diğer') setCustomFileType('');
                                if (newValue?.name !== 'Bağlantı') setLinkInfo('');
                                if (newValue?.name === 'Bağlantı') setPreviewFile(null);
                            }}
                            ListboxProps={{ style: { fontSize: baseFontSize, lineHeight: 1 } }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                    label="Dosya tipi seçiniz"
                                    placeholder="Dosya tipi seçiniz"
                                    InputProps={{ ...params.InputProps, style: { fontSize: baseFontSize } }}
                                />
                            )}
                            sx={{ width: '100%' }}
                        />
                    </Grid>

                    {(selectedFileType?.name === 'Diğer' || selectedFileType?.name === 'Bağlantı') && (
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label={selectedFileType?.name === 'Diğer' ? "Dosya adını yazınız" : "Bağlantı ismini yazınız"}
                                placeholder={selectedFileType?.name === 'Diğer' ? "Dosya adını yazınız" : "Bağlantı ismini yazınız"}
                                value={customFileType}
                                onChange={(e) => setCustomFileType(e.target.value)}
                                InputProps={{ style: { fontSize: baseFontSize } }}
                                required
                                error={showValidation && customFileType.trim() === ''}
                                helperText={showValidation && customFileType.trim() === '' ? 'Bu alan zorunludur' : ''}
                            />
                        </Grid>
                    )}

                    {selectedFileType?.name === 'Bağlantı' && (
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Bağlantı adresi"
                                placeholder="Bağlantı adresi giriniz"
                                value={linkInfo}
                                onChange={(e) => setLinkInfo(e.target.value)}
                                InputProps={{ style: { fontSize: baseFontSize } }}
                                required
                                error={showValidation && linkInfo.trim() === ''}
                                helperText={showValidation && linkInfo.trim() === '' ? 'Bu alan zorunludur' : ''}
                            />
                        </Grid>
                    )}

                    {selectedFileType?.name !== 'Bağlantı' && (
                        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'left' }}>
                            <DzPreviews
                                disabled={!selectedFileType}
                                title=""
                                size="small"
                                uploadSize="single"
                                onChange={setPreviewFile}
                                value={previewFile}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Div>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                disabled={selectedFileType?.name === 'Bağlantı' ? !linkInfo : !previewFile}
                                onClick={uploadAgendaFile}
                            >
                                {loadingFile ? 'Yükleniyor...' : 'Yükle'}
                            </Button>
                        </Div>
                    </Grid>
                </Grid>
            </JumboCardQuick>

            <JumboCardQuick sx={{ marginTop: 2 }}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <AgendaFileListTable
                        agendaFileList={agendaFileList}
                        handleDeleteItem={handleDeleteFile}
                    />
                )}
            </JumboCardQuick>
        </>
    );
}

export default AgendaFileCard;
